import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

type RouteMeta = {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: string;
  alt ?: string;
};

const routesMeta: Record<string, RouteMeta> = {
  "/": {
    title: "Asign.art | Buy, sell, and showcase artwork online",
    description: "Buy, sell, and showcase artwork online",
  },
  "/login": {
    title: "Login | Sign In",
    description: "Sign in to your account",
  },
  "/register": {
    title: "Register | Create Account",
    description: "Create a new account",
  },
  "/contact": {
    title: "Contact Us | Get in Touch",
    description: "Get in touch with us",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Asign.art",
    description: "Learn about our privacy practices",
  },
  "/terms-and-conditions": {
    title: "Terms and Conditions | Asign.art",
    description: "Read our terms of service",
  },
  "/add-image-guidelines": {
    title: "Image Guidelines | Asign.art",
    description: "Learn about image upload rules",
  },
};

const getDynamicRouteMeta = (path: string): RouteMeta | null => {
  if (path === "/artist") {
    return {
      title: "Artist | Explore",
      description: "Learn more about this artist",
    };
  }
  if (path === "/business") {
    return {
      title: "Business | Explore",
      description: "Learn more about this business",
    };
  }
  if (path === "/collectors") {
    return {
      title: "Collectors | Explore",
      description: "Learn more about collectors",
    };
  }
  if (path === "/everyone") {
    return {
      title: "Everyone | Explore",
      description: "Learn more about everyone",
    };
  }
  return null;
};

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

const SEO: React.FC<{ meta?: RouteMeta }> = ({ meta }) => {
  const { pathname } = useLocation();

  const defaultMeta: RouteMeta = {
    title: "Asign - Register, Buy, And Sell Artwork Online",
    description: "Buy, sell, and showcase artwork online. Asign is the ultimate platform for artists, collectors, and businesses to manage and trade art securely.",
    url : "https://www.asign.art",
    type : "website",
    image : window.location.origin + "/logo1.png",
    alt : "Asign Logo - The Ultimate Art Platform"
  };

  const routeMeta = routesMeta[pathname] || getDynamicRouteMeta(pathname);
  const finalMeta = meta ? meta : routeMeta ? routeMeta : defaultMeta;
  console.log("finalMeta", finalMeta);
  useEffect(() => {
    document.title = finalMeta.title;
  }, [finalMeta.title, pathname]);

  useEffect(() => {
    const loadScript = (id: string) => {
      if (!id) return;
      const script = document.createElement("script");
      script.async = true;
      script.id = "google-analytics";
      script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
      if (!document.getElementById("google-analytics")) {
        document.head.appendChild(script);
        script.onload = () => {
          window.dataLayer = window.dataLayer || [];
          window.gtag = function gtag() {
            window.dataLayer.push(arguments);
          };
          window.gtag("js", new Date());
          window.gtag("config", id, { anonymize_ip: true });
        };
      }
    };

    const loadGSCmetatag = (id: string) => {
      if (!id) return;
      const meta = document.createElement("meta");
      meta.id = "google-site-verification";
      meta.name = "google-site-verification";
      meta.content = id;
      if (!document.getElementById("google-site-verification")) {
        document.head.appendChild(meta);
      }
    };

    // Load Google Analytics
    if (import.meta.env.VITE_GA_CODE) {
      loadScript(import.meta.env.VITE_GA_CODE);
    }

    // Load Google Search Console
    if (import.meta.env.VITE_GSC) {
      loadGSCmetatag(import.meta.env.VITE_GSC);
    }
  }, []);

  const absoluteImage =
    meta?.image && !meta?.image.startsWith("http")
      ? `${window.location.origin}${meta?.image.startsWith("/") ? "" : "/"}${
          meta?.image
        }`
      : meta?.image;

  return (
    <Helmet>
      <title>{meta?.title ? meta.title : "Asign Artwork"}</title>
      <meta
        name="description"
        content={meta?.description ? meta.description : "View detailed artwork on Asign."}
      />
      <meta property="og:title" content={meta?.title ? meta.title : "Asign Artwork"} />
      <meta
        property="og:description"
        content={meta?.description ? meta.description : "Check out this artwork on Asign."}
      />
      <meta property="og:type" content={meta?.type ? "website" : "article"} />
      <meta
        property="og:image"
        content={finalMeta?.image ?? `${window.location.origin}/logo.png`}
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:url"
        content={finalMeta.url ?? `${window.location.origin}${window.location.pathname}`}
      />
    </Helmet>
  );
};

export default SEO;
